import React, { useMemo, useState } from "react";
import { STATUS_META, isOverdue, todayStr } from "@/lib/constants";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const CalendarView = ({ tasks, onOpenTask }) => {
  const [cursor, setCursor] = useState(() => new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const byDay = useMemo(() => {
    const m = {};
    tasks.forEach((t) => { if (t.end_date) (m[t.end_date] = m[t.end_date] || []).push(t); });
    return m;
  }, [tasks]);

  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const start = first.getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < start; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const todayIso = todayStr();

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">{MONTHS[month]} {year}</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-sm border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-2 text-xs font-medium rounded-sm border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-today">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-sm border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-t border-l border-zinc-200">
        {WD.map((d) => (
          <div key={d} className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 px-2 py-2 border-r border-b border-zinc-200 bg-zinc-50">{d}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? iso(d) : `empty-${i}`;
          const list = d ? (byDay[key] || []) : [];
          return (
            <div key={key} className={`min-h-[104px] p-1.5 border-r border-b border-zinc-200 ${d ? "" : "bg-zinc-50/50"}`} data-testid={d ? `calendar-day-${key}` : undefined}>
              {d && (
                <div className={`text-[11px] mb-1 ${key === todayIso ? "font-bold text-zinc-900" : "text-zinc-400"}`}>
                  {key === todayIso ? <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-zinc-900 text-white">{d.getDate()}</span> : d.getDate()}
                </div>
              )}
              <div className="space-y-1">
                {list.slice(0, 3).map((t) => {
                  const c = STATUS_META[t.status];
                  return (
                    <button key={t.task_id} onClick={() => onOpenTask(t)} data-testid={`calendar-task-${t.task_id}`}
                      className="w-full text-left truncate rounded-sm px-1.5 py-0.5 text-[10px] font-medium hover:opacity-80 transition-opacity"
                      style={{ background: c.bg, color: isOverdue(t) ? "#E11D48" : c.color }}>
                      {t.name}
                    </button>
                  );
                })}
                {list.length > 3 && <span className="text-[10px] text-zinc-400 px-1.5">+{list.length - 3} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
